import { useEffect, useRef, useState } from "react";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import Select from "../../../../components/form/Select";
import TextArea from "../../../../components/form/TextArea";
import TextBox from "../../../../components/form/TextBox";
import { API_ROUTES } from "../../../../core/Constants";
import { AutomationActions, AutomationEvents } from "../../../../core/Enums";
import { useHub } from "../../../../core/Global";
import DataItem from "../../../../models/DataItem";
import AutomationRuleItem from "../models/AutomationRuleItem";
import { AUTOMATION_ACTIONS, AUTOMATION_EVENTS } from "../models/AutomationLabels";


interface IProps {
    model?: AutomationRuleItem
    onClose?: () => void
    onSave?: () => void
}

export default function AutomationRuleForm(props: IProps) {

    const hub = useHub();

    // Closing is driven from here rather than left to DrawerForm: the drawer has to be shut by
    // script (which takes the backdrop with it) before the list unmounts this component.
    const drawerRef = useRef<DrawerFormRef>(null);

    // Two status lists on purpose: a rule may REACT to a job being closed or canceled, but it
    // may not MOVE one there — that stays a human decision.
    const [jobStatuses, jobStatusesSet] = useState<any[]>([]);
    const [targetStatuses, targetStatusesSet] = useState<any[]>([]);
    const [jobTypes, jobTypesSet] = useState<any[]>([]);
    const [jobSources, jobSourcesSet] = useState<any[]>([]);
    const [techs, techsSet] = useState<any[]>([]);
    const [metros, metrosSet] = useState<any[]>([]);
    const [officeUsers, officeUsersSet] = useState<any[]>([]);

    // Single-select <Select> renders no form control (only the multi one posts a hidden input),
    // so these three are held here and merged into the body on submit.
    const [selectedEvent, selectedEventSet] = useState<string>(props.model?.event?.toString() ?? "");
    const [selectedAction, selectedActionSet] = useState<string>(props.model?.action?.toString() ?? "");
    const [targetStatusId, targetStatusIdSet] = useState<string>(props.model?.targetJobStatusId ?? "");

    const Events: DataItem[] = AUTOMATION_EVENTS.map(e => ({ id: e.id.toString(), name: e.name }));
    const Actions: DataItem[] = AUTOMATION_ACTIONS.map(a => ({ id: a.id.toString(), name: a.name }));

    useEffect(() => {
        hub.Get(API_ROUTES.TagifyJobStatuses, res => jobStatusesSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyJobStatuses + "?excludeClosedAndCanceled=true", res => targetStatusesSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyJobTypes, res => jobTypesSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyJobSources, res => jobSourcesSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyTechnicians, res => techsSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyMetros, res => metrosSet(res.data ?? []));
        hub.Get(API_ROUTES.TagifyAdminsAndAgents, res => officeUsersSet(res.data ?? []));
    }, [])

    const action = Number.parseInt(selectedAction);
    const isChangeStatus = action === AutomationActions.ChangeJobStatus;
    const isSendToTech = action === AutomationActions.SendJobToTechnician;
    const isNotification = action === AutomationActions.SendNotification;
    const isMessage = action === AutomationActions.SendMessageToTechnician || action === AutomationActions.SendMessageToCustomer;
    const needsText = isMessage || isNotification;

    // Which parameter the chosen event takes, if any.
    const eventParam = AUTOMATION_EVENTS.find(e => e.id.toString() === selectedEvent)?.param;
    const needsMinutes = eventParam === "minutesAfterAccept" || eventParam === "minutesBeforeAppt";

    const isEdit = !!props.model?.id;

    return (
        <DrawerForm ref={drawerRef} id="drawer-automation-rule" size="medium"
            title={isEdit ? "Edit Automation Rule" : "Create Automation Rule"}
            buttonText={isEdit ? "Update Rule" : "Create Rule"}
            onOpen={() => { $("#txt-automationrule-name").focus(); }}
            onClose={props.onClose}
            onSubmit={(body, callback) => {

                window.hideFormErrors();
                var valid = true;
                var focus = true;

                if (!body.name || body.name.trim().length < 2) {
                    window.formError("name", "Please write a valid name!", undefined, focus);
                    valid = false;
                    focus = false;
                }

                if (window.isNullOrEmpty(selectedEvent)) {
                    window.formError("event", "Please choose an event!", undefined, focus);
                    valid = false;
                    focus = false;
                }

                if (window.isNullOrEmpty(selectedAction)) {
                    window.formError("action", "Please choose an action!", undefined, focus);
                    valid = false;
                    focus = false;
                }

                if (needsMinutes) {
                    const minutes = Number.parseInt(body.eventMinutes);
                    if (!minutes || minutes < 1) {
                        window.formError("eventMinutes", "Please enter a valid number of minutes!", undefined, focus);
                        valid = false;
                        focus = false;
                    }
                }

                if (isChangeStatus && window.isNullOrEmpty(targetStatusId)) {
                    window.formError("targetJobStatusId", "Please choose the status to move the job to!", undefined, focus);
                    valid = false;
                    focus = false;
                }

                if (needsText && (!body.messageText || body.messageText.trim().length < 2)) {
                    window.formError("messageText", "Please write the message!", undefined, focus);
                    valid = false;
                    focus = false;
                }

                if (!valid) {
                    callback!();
                    return;
                }

                const payload = {
                    ...body,
                    event: selectedEvent,
                    action: selectedAction,
                    targetJobStatusId: targetStatusId
                };

                const done = (res?: any) => {

                    if (!res?.success) {
                        // Form only paints an error when the server names a FIELD — its
                        // no-field branch is commented out, so a server exception comes back
                        // completely silent and reads as "the button did nothing". Surface it.
                        if (!res?.field) {
                            const detail = (res as any)?.error?.message;
                            toastError(detail
                                ? `Couldn't save the automation rule: ${detail}`
                                : (res?.message ?? "Couldn't save the automation rule."));
                        }

                        // Hand the response back so the form paints the field error and the
                        // button comes out of its busy state.
                        callback!(res);
                        return;
                    }

                    toastSuccess(isEdit
                        ? "Automation rule has been updated successfully!"
                        : "Automation rule has been created successfully!");

                    // Reload the table first, then shut the drawer by script. callback() is
                    // called with nothing so DrawerForm only clears the busy state and leaves
                    // the closing to us; the list unmounts this form from onClose.
                    if (props.onSave) props.onSave();
                    drawerRef.current?.close();
                    callback!();
                }

                // Without an error handler a failed request never calls back at all and the
                // button spins forever, which is exactly how a saved-but-unreported rule looks.
                const failed = () => {
                    toastError("Couldn't save the automation rule. Please try again.");
                    callback!();
                }

                if (isEdit) hub.Put(API_ROUTES.AutomationRules + props.model!.id, payload, done, failed);
                else hub.Post(API_ROUTES.AutomationRules, payload, done, failed);

            }}>

            <div className="mb-10">

                <TextBox id="txt-automationrule-name" name="name" label="Rule Name *" labelUp block
                    marginBottom={5} placeholder="e.g. Move to In Progress once the tech calls"
                    defaultValue={props.model?.name} />

                <div className="separator my-6"></div>

                {/* The section heading is the field's label, so the Select carries no label of its
                    own — it only keeps the error span the heading would otherwise host. */}
                <div className="fw-bold fs-7 text-gray-800 mb-2">
                    <Icon icon="bolt" className="me-2" size={12} />
                    When
                    <span id="event-err" className="small text-danger mx-3 form-error"></span>
                </div>

                <Select id="sel-automationrule-event" name="event" labelUp block
                    marginBottom={5} deselect={false} placeholder="Choose the event that fires this rule"
                    source={Events} value={selectedEvent}
                    onSelect={val => selectedEventSet(val ?? "")} />

                {
                    eventParam === "statuses" &&
                    <Select id="sel-automationrule-eventstatuses" name="eventJobStatusIds" multiple
                        label="Changed to" labelUp block marginBottom={5}
                        placeholder="Any status"
                        source={jobStatuses} dataText="title" dataValue="value"
                        defaultValues={props.model?.eventJobStatusIds} />
                }

                {
                    needsMinutes &&
                    <div className="w-350px">
                        {/* The appended text says what the number means, so the field carries no
                            label. `labelUp` is left off on purpose — TextBox only renders its
                            error span outside the label in that branch. */}
                        <TextBox id="txt-automationrule-eventminutes" name="eventMinutes" numeric maxLength={4}
                            block marginBottom={5}
                            iconText={eventParam === "minutesAfterAccept"
                                ? "Minutes After Job Accepted!"
                                : "Minutes Before Appointment!"}
                            defaultValue={props.model?.eventMinutes?.toString()
                                ?? (eventParam === "minutesAfterAccept" ? "30" : "60")} />
                    </div>
                }

                <div className="separator my-6"></div>

                <div className="fw-bold fs-7 text-gray-800 mb-2">
                    <Icon icon="wand-magic-sparkles" className="me-2" size={12} />
                    Then
                    <span id="action-err" className="small text-danger mx-3 form-error"></span>
                </div>

                <Select id="sel-automationrule-action" name="action" labelUp block
                    marginBottom={5} deselect={false} placeholder="Choose what the rule does"
                    source={Actions} value={selectedAction}
                    onSelect={val => selectedActionSet(val ?? "")} />

                {
                    isChangeStatus &&
                    <Select id="sel-automationrule-status" name="targetJobStatusId" label="Move the job to *" labelUp block
                        marginBottom={5} placeholder="Choose a job status"
                        source={targetStatuses} dataText="title" dataValue="value"
                        value={targetStatusId}
                        onSelect={val => targetStatusIdSet(val ?? "")} />
                }

                {
                    isSendToTech &&
                    <FlexRow className="bg-light-secondary p-3 rounded mb-5">
                        <Icon icon="info-circle" className="text-gray-600 me-2" size={12} />
                        <span className="fs-8 text-gray-700">
                            The job is sent to the technician already assigned to it, using the account's
                            Send Job settings.
                        </span>
                    </FlexRow>
                }

                {
                    isNotification &&
                    <>
                        <TextBox id="txt-automationrule-notiftitle" name="notificationTitle"
                            label="Notification Title" labelUp block marginBottom={5}
                            defaultValue={props.model?.notificationTitle} />

                        <Select id="sel-automationrule-notifyusers" name="notifyUserIds" multiple
                            label="Notify" labelUp block marginBottom={5}
                            placeholder="Leave it blank to notify everyone who can see the job"
                            source={officeUsers} dataText="title" dataValue="value"
                            defaultValues={props.model?.notifyUserIds} />
                    </>
                }

                {
                    needsText &&
                    <TextArea name="messageText" label={isNotification ? "Notification Text *" : "Message *"}
                        labelUp block rows={4} marginBottom={5}
                        value={props.model?.messageText} />
                }

                <div className="separator my-6"></div>

                <div className="fw-bold fs-7 text-gray-800 mb-1">
                    <Icon icon="filter" className="me-2" size={12} />
                    Only if
                </div>
                <div className="fs-8 text-gray-600 mb-3">
                    Every condition is optional. Leave one blank and the rule is not limited by it.
                </div>

                <Select id="sel-automationrule-jobtypes" name="jobTypeIds" multiple
                    label="Job Type(s)" labelUp block marginBottom={5} placeholder="Any job type"
                    source={jobTypes} dataText="title" dataValue="value"
                    defaultValues={props.model?.jobTypeIds} />

                <Select id="sel-automationrule-jobsources" name="jobSourceIds" multiple
                    label="Job Source(s)" labelUp block marginBottom={5} placeholder="Any job source"
                    source={jobSources} dataText="title" dataValue="value"
                    defaultValues={props.model?.jobSourceIds} />

                <Select id="sel-automationrule-techs" name="techIds" multiple
                    label="Technician(s)" labelUp block marginBottom={5} placeholder="Any technician"
                    source={techs} dataText="title" dataValue="value"
                    defaultValues={props.model?.techIds} />

                <Select id="sel-automationrule-metros" name="metroIds" multiple
                    label="Metro(s)" labelUp block marginBottom={5} placeholder="Any metro"
                    source={metros} dataText="title" dataValue="value"
                    defaultValues={props.model?.metroIds} />

                <div className="separator my-6"></div>

                <TextArea name="description" label="Description" labelUp block rows={2} marginBottom={5}
                    value={props.model?.description} />

                <FlexRow className="mt-6">
                    <Icon icon="info-circle" className="fs-9 text-gray-600 me-1" />
                    <span className="fs-9 text-gray-600">
                        Rules are saved but not executed yet — running them is a separate step.
                    </span>
                </FlexRow>

            </div>

        </DrawerForm>
    )
}
